import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutGrid, Briefcase, ListChecks, Calendar as CalIcon, BarChart3, Settings as Cog, LogOut, Sparkles, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const navItems = [
  { to: "/app", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/app/jobs", label: "Postes", icon: Briefcase, testid: "nav-jobs" },
  { to: "/app/talent", label: "Pool talents", icon: Users, testid: "nav-talent" },
  { to: "/app/actions", label: "Actions", icon: ListChecks, testid: "nav-actions" },
  { to: "/app/calendar", label: "Calendrier", icon: CalIcon, testid: "nav-calendar" },
  { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/app/settings", label: "Réglages", icon: Cog, testid: "nav-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const isActive = (to) => to === "/app" ? loc.pathname === "/app" : loc.pathname.startsWith(to);
  const state = user?.access_state;
  const showTrialBanner = state === "trial_active" && user?.subscription_end;
  const daysLeft = user?.subscription_end
    ? Math.max(0, Math.ceil((new Date(user.subscription_end) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="min-h-screen flex bg-[#F8FAFC] text-slate-900">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-slate-200">
          <Link to="/app" className="flex items-center gap-2 group" data-testid="sidebar-logo">
            <div className="w-9 h-9 bg-[#0F172A] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="font-display text-xl tracking-tight">Xpert Recruit AI</div>
          </Link>
          <div className="mt-3 text-[11px] tracking-[0.2em] uppercase text-slate-400">Copilote recruteur</div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((it) => {
            const Icon = it.icon;
            const active = isActive(it.to);
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                  active
                    ? "bg-[#0F172A] text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{it.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-2">Compte</div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              className="p-2 hover:bg-slate-100 rounded"
              data-testid="logout-btn"
              title="Déconnexion"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-3">
            {user?.subscription_status === "active" ? (
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid="subscription-badge">Abonnement actif</Badge>
            ) : (
              <Link to="/pricing" data-testid="subscription-badge-upgrade">
                <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200">Essai · Passer Pro</Badge>
              </Link>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {showTrialBanner && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 text-sm text-amber-900 flex items-center justify-between flex-wrap gap-2">
            <span data-testid="trial-banner">
              🎁 <strong>Essai gratuit</strong> · Il vous reste <strong className="tabular-nums">{daysLeft} jour(s)</strong> avant la fin de votre essai.
            </span>
            <Link to="/pricing" className="underline font-medium hover:text-amber-700" data-testid="trial-banner-upgrade">Passer Pro maintenant →</Link>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
